import { Box, Button, Typography } from "@mui/material";
import { AMBER, FAINT, LINE, MONO, MUTED, btnPrimary, capSx, floatSx, pillSx, swatchSx } from "./editorTheme";
import { fmtArea, fmtDate, geometryAreaM2 } from "./editorModel";
import { jobOpen, useEditorData } from "./useEditorData";
import { useEditorActions } from "./useEditorActions";

const MAX_LEGEND = 12;

/** The editor's home panel: the legend of what the layer holds, the two
 *  ways to change it, and every refinement made so far. */
export default function VegEditorHome() {
  const d = useEditorData();
  const a = useEditorActions();
  const legend = d.vegArea?.class_areas ?? [];
  const shown = legend.slice(0, MAX_LEGEND);
  const statusOf = (j: typeof d.jobs[number]): { text: string; tone: "ok" | "run" | "plain" } => {
    if (j.reverting) return { text: "Undoing", tone: "run" };
    if (j.status === "applied") return { text: "Applied", tone: "ok" };
    if (j.status === "failed") return { text: "Failed", tone: "plain" };
    if (j.status === "reverted") return { text: "Undone", tone: "plain" };
    if (j.status === "applying") return { text: "Applying", tone: "run" };
    return { text: "In progress", tone: "run" };
  };
  return (
    <Box sx={{ ...floatSx, position: "fixed", top: "calc(var(--topbar-h) + var(--strategies-h) + 12px)", left: 12, width: 340, maxHeight: "calc(var(--vh100) - var(--topbar-h) - var(--strategies-h) - 24px)", overflowY: "auto", zIndex: 1100 }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: AMBER }} />
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Vegetation</Typography>
          </Box>
          {!!d.vegArea?.grid_m && <Typography sx={{ fontSize: 12, color: MUTED }}>{d.vegArea.grid_m} m grid</Typography>}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9, mt: 1.5 }}>
          {!legend.length && <Typography sx={{ fontSize: 12.5, color: MUTED }}>Loading the legend…</Typography>}
          {shown.map((c) => (
            <Box key={c.veg_class} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 12, minWidth: 0 }} title={c.veg_class}>
              <Box sx={swatchSx(c.color)} />
              <Typography sx={{ fontSize: 12, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.veg_class}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>{c.area_ha} ha</Typography>
            </Box>
          ))}
          {legend.length > MAX_LEGEND && <Typography sx={{ fontSize: 11.5, color: FAINT }}>+{legend.length - MAX_LEGEND} more</Typography>}
          {!!d.vegArea?.unclassified_ha && d.vegArea.unclassified_ha >= 0.5 && (
            <Typography sx={{ fontSize: 11.5, color: AMBER }}>{d.vegArea.unclassified_ha} ha has no vegetation data</Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 1.75 }}>
          <Button sx={{ ...btnPrimary, flex: 1 }} onClick={a.newRefinement} disabled={!!d.resumable && d.resumable.status !== "review"}>Refine with imagery</Button>
        </Box>
      </Box>
      <Box sx={{ borderTop: `1px solid ${LINE}`, p: "12px 8px 10px" }}>
        <Typography sx={{ ...capSx, px: 1, pb: 0.75 }}>Refinements</Typography>
        {d.jobs.map((j) => {
          const s = statusOf(j);
          const open = jobOpen(j) || j.status === "failed";
          return (
            <Box key={j.id} onClick={open ? () => a.openSession(j.id) : undefined}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, p: "8px 10px", borderRadius: 2, cursor: open ? "pointer" : "default", "&:hover": { bgcolor: "rgba(255,255,255,0.06)" } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{j.name}</Typography>
                <Typography sx={{ fontSize: 12, color: MUTED }}>
                  {j.area ? fmtArea(geometryAreaM2(j.area)) : "Whole project"} · {fmtDate(j.applied_at ?? j.created_at)}{j.options?.start === "clean" ? " · clean slate" : ""}
                </Typography>
              </Box>
              {j.status === "applied" && !j.reverting ? (
                <Button size="small" sx={{ textTransform: "none", color: MUTED, fontSize: 12, minWidth: 0 }} onClick={(ev) => { ev.stopPropagation(); a.undoApplied(j.id); }}>Undo</Button>
              ) : j.status === "failed" ? (
                <Button size="small" title={j.error ?? ""} sx={{ textTransform: "none", color: MUTED, fontSize: 12, minWidth: 0 }} onClick={(ev) => { ev.stopPropagation(); a.discardJob(j.id); }}>Discard</Button>
              ) : null}
              <Box sx={pillSx(s.tone)}>{s.text}</Box>
            </Box>
          );
        })}
        {!d.jobs.length && <Typography sx={{ fontSize: 12, color: MUTED, px: 1, pb: 0.5 }}>None yet.</Typography>}
      </Box>
    </Box>
  );
}
